import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, computed, effect, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { AppSettings, Settings } from './app.settings';
import { ConfigService, UsageAnalyticsService } from '@core/services';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- Root component intentionally uses body selector
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [NgClass, RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly configService = inject(ConfigService);
  private readonly usageAnalytics = inject(UsageAnalyticsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly appSettings = inject(AppSettings);

  settings!: Settings;

  // Signals - prefix with "signal" when read in templates (no-call-expression allowPrefix)
  readonly signalConfigurationLoaded = computed(() => !!this.configService.config()?.apiUri);
  readonly signalThemeName = computed(() => this.configService.config()?.theme ?? '');

  // DOM elements (these can't be signals as they're external to Angular)
  private readonly favicon: HTMLLinkElement | null = document.querySelector('#clientFavicon');
  private readonly title: HTMLElement | null = document.querySelector('#clientTitle');

  constructor() {
    this.settings = this.appSettings.settings;

    // Effect to update DOM elements when config changes
    effect(() => {
      const config = this.configService.config();
      if (!config?.apiUri) {
        return;
      }
      if (this.title) {
        this.title.innerHTML = config.client + ' ' + config.appName;
      }
      if (this.favicon) {
        this.favicon.href = config.favIcon;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.configService.loadConfig();
      this.usageAnalytics.startTracking();
      console.debug('Config loaded, configuration check:', this.signalConfigurationLoaded());
      // Explicitly trigger change detection after config loads
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Failed to load configuration:', error);
    }
  }
}
